/**
 * Error handling utilities for user-friendly error messages
 */

export interface ErrorInfo {
  title: string;
  message: string;
  variant: 'destructive' | 'warning' | 'info';
  actionable?: boolean;
  retryable?: boolean;
}

/**
 * Maps technical error messages to user-friendly ones
 */
export const getErrorInfo = (error: any): ErrorInfo => {
  // Handle string errors
  if (typeof error === 'string') {
    return mapErrorMessage(error);
  }

  // Handle API response errors FIRST (before checking Error instance)
  // This is important because AxiosError extends Error, but we want to prioritize API response data
  let errorMessage = null;

  // Try different possible locations for the error message
  if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.error) {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.details) {
    errorMessage = error.response.data.details;
  } else if (error?.response?.data) {
    // If data is a string, use it directly
    if (typeof error.response.data === 'string') {
      errorMessage = error.response.data;
    }
  }

  // If we found an API error message, map it
  if (errorMessage) {
    return mapErrorMessage(errorMessage);
  }

  // Handle Error objects (fallback for non-API errors)
  if (error instanceof Error) {
    return mapErrorMessage(error.message);
  }

  // Final fallback to error.message if available
  if (error?.message) {
    return mapErrorMessage(error.message);
  }

  // Handle network errors
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return {
      title: 'Service Unavailable',
      message: 'Unable to connect to the authentication service. Please try again in a few moments.',
      variant: 'destructive',
      retryable: true
    };
  }

  // Handle timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      title: 'Request Timeout',
      message: 'The request took too long to complete. Please try again.',
      variant: 'warning',
      retryable: true
    };
  }

  // Handle 502 Bad Gateway (service unavailable)
  if (error?.response?.status === 502) {
    return {
      title: 'Service Connection Error',
      message: 'Unable to connect to the authentication service. Please try again.',
      variant: 'destructive',
      retryable: true
    };
  }

  // Handle 503 Service Unavailable
  if (error?.response?.status === 503) {
    return {
      title: 'Service Temporarily Unavailable',
      message: 'Authentication service is temporarily down. Please try again later.',
      variant: 'destructive',
      retryable: true
    };
  }

  // Handle 401 Unauthorized
  if (error?.response?.status === 401) {
    return {
      title: 'Authentication Required',
      message: 'Your session has expired. Please log in again.',
      variant: 'warning',
      actionable: true
    };
  }

  // Handle 403 Forbidden
  if (error?.response?.status === 403) {
    return {
      title: 'Access Denied',
      message: 'You do not have permission to perform this action.',
      variant: 'destructive'
    };
  }

  // Handle 400 Bad Request - often contains detailed validation errors
  if (error?.response?.status === 400) {
    // Try to extract the detailed error message
    let errorMessage = null;

    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error?.response?.data?.details) {
      errorMessage = error.response.data.details;
    } else if (typeof error?.response?.data === 'string') {
      errorMessage = error.response.data;
    }

    if (errorMessage) {
      return mapErrorMessage(errorMessage);
    }

    // Fallback for 400 errors without detailed message
    return {
      title: 'Invalid Request',
      message: 'The request contains invalid data. Please check your input and try again.',
      variant: 'warning'
    };
  }

  // Handle 404 Not Found
  if (error?.response?.status === 404) {
    return {
      title: 'Not Found',
      message: 'The requested resource could not be found.',
      variant: 'warning'
    };
  }

  // Handle 500 Server Error
  if (error?.response?.status >= 500) {
    return {
      title: 'Server Error',
      message: 'Something went wrong on our end. Please try again in a few moments.',
      variant: 'destructive',
      retryable: true
    };
  }

  // Default fallback
  return {
    title: 'Unexpected Error',
    message: 'Something went wrong. Please try again.',
    variant: 'destructive',
    retryable: true
  };
};

/**
 * Maps specific error messages to user-friendly versions
 */
const mapErrorMessage = (message: string): ErrorInfo => {
  const lowerMessage = message.toLowerCase();

  // Authentication errors
  if (lowerMessage.includes('invalid email') || lowerMessage.includes('invalid password') || lowerMessage.includes('invalid credentials')) {
    return {
      title: 'Login Failed',
      message: 'The email, password, or institution code you entered is incorrect. Please check your details and try again.',
      variant: 'destructive'
    };
  }

  if (lowerMessage.includes('invalid institution code') || lowerMessage.includes('invalid tenant')) {
    return {
      title: 'Invalid Institution Code',
      message: 'The institution code you entered is not recognized. Please check with your administrator.',
      variant: 'destructive'
    };
  }

  // Test creation / question-pool validation errors
  if (lowerMessage.includes('question') &&
      (lowerMessage.includes('required') || lowerMessage.includes('available') || lowerMessage.includes('augmentation'))) {
    return {
      title: 'Question Selection Issue',
      message,
      variant: 'warning'
    };
  }

  // Validation errors
  if (lowerMessage.includes('required') || lowerMessage.includes('missing')) {
    return {
      title: 'Missing Information',
      message: 'Please fill in all required fields before continuing.',
      variant: 'warning'
    };
  }

  if (lowerMessage.includes('invalid email')) {
    return {
      title: 'Invalid Email',
      message: 'Please enter a valid email address.',
      variant: 'warning'
    };
  }

  if (lowerMessage.includes('password') && lowerMessage.includes('weak')) {
    return {
      title: 'Weak Password',
      message: 'Your password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.',
      variant: 'warning'
    };
  }

  if (lowerMessage.includes('passwords do not match')) {
    return {
      title: 'Password Mismatch',
      message: 'The passwords you entered do not match. Please try again.',
      variant: 'warning'
    };
  }

  // Duplicate errors
  if (lowerMessage.includes('already exists') || lowerMessage.includes('duplicate')) {
    return {
      title: 'Already Exists',
      message: 'A record with this information already exists. Please use different details.',
      variant: 'warning'
    };
  }

  // Permission errors
  if (lowerMessage.includes('permission') || lowerMessage.includes('unauthorized')) {
    return {
      title: 'Permission Denied',
      message: 'You do not have permission to perform this action. Please contact your administrator.',
      variant: 'destructive'
    };
  }

  // Data errors
  if (lowerMessage.includes('not found')) {
    return {
      title: 'Not Found',
      message: 'The requested information could not be found. It may have been deleted or moved.',
      variant: 'warning'
    };
  }

  // Test creation specific errors
  if (lowerMessage.includes('insufficient questions')) {
    // Check if the message contains specific details about the shortage
    if (lowerMessage.includes('need') && lowerMessage.includes('but only') && lowerMessage.includes('available')) {
      // Return the detailed message as-is since it's already user-friendly
      return {
        title: 'Insufficient Questions Available',
        message: message, // Use the original detailed message
        variant: 'warning',
        actionable: true
      };
    }

    return {
      title: 'Insufficient Questions',
      message: 'There are not enough questions available to create this test. Please add more questions or adjust the test configuration.',
      variant: 'warning',
      actionable: true
    };
  }

  // Subject-specific question shortage
  if (lowerMessage.includes('subject') && (lowerMessage.includes('need') || lowerMessage.includes('available'))) {
    return {
      title: 'Subject Question Shortage',
      message: message, // Use the detailed message from the server
      variant: 'warning',
      actionable: true
    };
  }

  // Test configuration errors
  if (lowerMessage.includes('invalid test configuration') || lowerMessage.includes('test configuration')) {
    return {
      title: 'Invalid Test Configuration',
      message: 'The test configuration is invalid. Please check your settings and try again.',
      variant: 'warning',
      actionable: true
    };
  }

  // File upload errors
  if (lowerMessage.includes('file too large') || lowerMessage.includes('file size')) {
    return {
      title: 'File Too Large',
      message: 'The file you selected is too large. Please choose a smaller file.',
      variant: 'warning'
    };
  }

  if (lowerMessage.includes('invalid file format') || lowerMessage.includes('unsupported file')) {
    return {
      title: 'Invalid File Format',
      message: 'The file format is not supported. Please use a CSV or Excel file.',
      variant: 'warning'
    };
  }

  // Email errors
  if (lowerMessage.includes('email failed') || lowerMessage.includes('email not sent')) {
    return {
      title: 'Email Delivery Failed',
      message: 'We were unable to send the email. Please check the email address and try again.',
      variant: 'warning',
      retryable: true
    };
  }

  // Database errors
  if (lowerMessage.includes('database') || lowerMessage.includes('connection')) {
    return {
      title: 'Database Error',
      message: 'We are experiencing technical difficulties. Please try again in a few moments.',
      variant: 'destructive',
      retryable: true
    };
  }

  // Default case - return the original message but with a friendly title
  return {
    title: 'Error',
    message: message,
    variant: 'destructive'
  };
};

/**
 * Scrolls to and focuses on an error element
 */
export const scrollToError = (elementId?: string) => {
  const element = elementId
    ? document.getElementById(elementId)
    : document.querySelector('[role="alert"]');

  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });

    // Focus for accessibility
    if ((element as HTMLElement).tabIndex >= 0) {
      (element as HTMLElement).focus();
    }
  }
};

/**
 * Creates a unique error ID for tracking
 */
export const generateErrorId = () => {
  return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
